import { detectCards } from "./detect.js";
import { OPENCV_URL } from "./constants.js";

/* ===========================================================================
   The worker, from the page's side.

   Detection is hundreds of milliseconds of straight-line arithmetic, and on the main
   thread that is not merely slow, it is *janky*: requestAnimationFrame cannot paint
   while it runs, so the live preview freezes for the whole pass and the page stops
   answering taps. Moving it off does not make it faster, it makes it invisible.

   The same worker also owns ORB verification, because both want OpenCV and one copy of
   the WebAssembly heap is the difference between working and not working on a phone.
   =========================================================================== */

//: One owner, because an ES module cannot assign to a binding it imported and the
//: worker's own `onerror` has to be able to drop the handle when the thread dies.
let worker = null;
let sequence = 0;
const pending = new Map();

//: Which kernels detection is running on, as last reported by the worker. Shown in the
//: boot list, and the only way the page can say "OpenCV" and mean it.
export const cvState = { backend: "scalar", loading: false, error: null };

export const workerReady = () => worker !== null;

export function startWorker() {
  try {
    // Resolved against this module's URL rather than the document's, so the worker is
    // found whatever path the page itself was served from.
    const created = new Worker(new URL("./cv.worker.js", import.meta.url), { type: "module" });
    created.onmessage = (event) => {
      const { id, error, backend } = event.data;
      if (backend) cvState.backend = backend;
      const waiting = pending.get(id);
      if (!waiting) return;
      pending.delete(id);
      if (error) waiting.reject(new Error(error));
      else waiting.resolve(event.data);
    };
    created.onerror = (event) => {
      console.warn("the scanner worker failed; falling back to the main thread", event.message);
      for (const waiting of pending.values()) {
        waiting.reject(new Error(event.message || "worker error"));
      }
      pending.clear();
      worker = null;
    };
    worker = created;
    return created;
  } catch (error) {
    console.warn("could not start the scanner worker:", error);
    worker = null;
    return null;
  }
}

function post(message, transfer = []) {
  const id = ++sequence;
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject });
    worker.postMessage({ ...message, id }, transfer);
  });
}

/**
 * Fetch OpenCV in the worker and switch detection onto it.
 *
 * Deliberately not awaited by anything on the scan path: detection answers on the scalar
 * kernels from the first frame, and the swap lands whenever the download does. A failure
 * is recorded and otherwise ignored — the page keeps working, a little slower.
 */
let cvRequest = null;

export function loadOpenCv(url = OPENCV_URL) {
  if (!worker) return Promise.resolve(cvState);
  // Memoised on the promise rather than on a flag: boot starts this for detection's sake
  // and the precision stage awaits the same load for ORB's, and the second caller has to
  // get the *result* rather than an early "already loading".
  if (cvRequest) return cvRequest;
  cvState.loading = true;
  cvRequest = post({ op: "loadOpenCv", url })
    .then((result) => {
      cvState.backend = result.backend || "opencv";
      return cvState;
    })
    .catch((error) => {
      cvState.error = error.message;
      cvRequest = null; // a later attempt may succeed
      console.warn("OpenCV could not be loaded; staying on the scalar kernels:", error.message);
      return cvState;
    })
    .finally(() => {
      cvState.loading = false;
    });
  return cvRequest;
}

/** Whether ORB verification can run — it needs the library the worker loads. */
export const orbReady = () => worker !== null && cvState.backend === "opencv";

/** Score a query crop against reference descriptors, in the worker. */
export function orbVerify(gray, width, height, references) {
  if (!orbReady()) return Promise.reject(new Error("OpenCV is not loaded"));
  return post({ op: "verify", gray, width, height, references });
}

/**
 * Detect off-thread, falling back to an inline call when no worker is available.
 *
 * `transfer` hands the pixel buffer over instead of copying it, which the live loop
 * wants because every frame is a fresh getImageData. The still path must *not* set it:
 * on a small image the detection buffer is the same array the warp samples from, and
 * transferring would detach it out from under stage 5.2.
 */
export function detectAsync(data, width, height, options, transfer = false) {
  if (!worker) return Promise.resolve(detectCards(data, width, height, options));
  return post({ op: "detect", data, width, height, options }, transfer ? [data.buffer] : [])
    .then((result) => result.quads.map((quad, i) => ({ quad, ...result.meta[i] })))
    .catch((error) => {
      // A worker that dies mid-scan must not take the scan with it.
      console.warn("worker detection failed; retrying inline:", error.message);
      return detectCards(data, width, height, options);
    });
}
